/**
 * /publishers/payout-overrides — publisher-payout-overrides operator page.
 *
 * Declares a TanStack Router route and renders the feature page. The root
 * wire-up cycle picks this route up after the prod merge (agents never touch
 * `router.tsx`).
 *
 * Search params reserved for `?affiliate=<id>` so operators can deep-link to
 * a specific affiliate's override list.
 */
import { createRoute, Link } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { PublisherPayoutOverridesPage } from "@/features/publisher-payout-overrides/PublisherPayoutOverridesPage";

// ---------------------------------------------------------------------------
// Route — mounts at `/publishers/payout-overrides`.
// ---------------------------------------------------------------------------

interface PayoutOverridesSearch {
	affiliate?: string;
}

const AFFILIATE_RE = /^[a-z0-9_-]{1,64}$/i;

export const publishersPayoutOverridesRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/publishers/payout-overrides",
	validateSearch: (raw: Record<string, unknown>): PayoutOverridesSearch => {
		const affiliate = raw.affiliate;
		if (typeof affiliate === "string" && AFFILIATE_RE.test(affiliate)) {
			return { affiliate };
		}
		return {};
	},
	component: PublishersPayoutOverridesRoute,
});

export default publishersPayoutOverridesRoute;

// ---------------------------------------------------------------------------
// Page shell — breadcrumb + live feature component.
// ---------------------------------------------------------------------------

export function PublishersPayoutOverridesRoute() {
	const { affiliate } = publishersPayoutOverridesRoute.useSearch();
	return (
		<div className="mx-auto flex w-full max-w-5xl flex-col gap-4 px-6 pt-6 sm:pt-10">
			<Breadcrumb />
			<PublisherPayoutOverridesPage initialAffiliateId={affiliate} />
		</div>
	);
}

function Breadcrumb() {
	return (
		<nav aria-label="Breadcrumb" className="text-sm text-zinc-500">
			<ol className="flex flex-wrap items-center gap-1">
				<li>
					<Link
						to="/"
						className="rounded-[6px] px-1 py-0.5 hover:bg-zinc-100 hover:text-zinc-900"
					>
						Dashboard
					</Link>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li>
					<span className="rounded-[6px] px-1 py-0.5">Publishers</span>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li className="text-zinc-900">Payout overrides</li>
			</ol>
		</nav>
	);
}
